import { useEffect, useRef, useState } from 'react';
import { Slate } from 'components/Slate';
import {
  createSessionID,
  formatNumberAsCurrency,
  getPathWithRef,
  getUrlParams,
  getUrlSearchParamsStrNoMethod,
  noop,
  scrollToTop,
} from 'helpers/utils';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getRecipientsData, getRecipientsState } from 'selectors/recipients';
import {
  clearAccessToken,
  clearDonationSelections,
  getSessionID,
  setSessionID,
} from 'services/storage';
import { getRecipients } from 'store/recipients/actions';
import { getContributionSummaries } from 'store/contributionSummary/slice';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { useIsOathCheckoutEnabled, useOathCheckout } from 'hooks/donate';
import {
  democracyEngineCheckoutRedirect,
  DONATE_SHOPPING_CART_PAGE_CATEGORY,
  computeDefaultSplitAllocation,
  engageLabsCheckoutRedirect,
  getCandidates,
  getCustomCheckoutLink,
  getDonationSelectionsObj,
  getMaxDonationAmountContributionLimits,
  getOverageMax,
  getProcessingRedirect,
  getSelectedCandidates,
  getSelectedCandidatesWithValidAmount,
  getSelectionsFromUrl,
  getShowCustomSplit,
  reconcileEvenSplitTotal,
  validateDonation,
} from 'helpers/donate';
import { getUserState } from 'selectors/user';
import { getUserProfile } from 'store/user/actions';
import { useAuth } from 'hooks/auth';
import { getDonateConfig } from 'selectors/siteConfig';
import { IPage, IPageType, ProcessingRedirect } from 'store/donatePage/types';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { getDonatePageData, getDonatePageState } from 'selectors/donatePage';
import { getDonatePage } from 'store/donatePage/actions';
import { EventSlate } from 'components/Slate/EventSlate';
import { IEvent, TicketWithQuantitySelected } from 'store/eventPage/types';
import { getEventData, getEventState } from 'selectors/eventPage';
import { getEvent } from 'store/eventPage/actions';
import { getTicketPriceTotal, getTicketsWithSelections } from 'helpers/event';
import { getPayDafData } from 'selectors/pay';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import HeaderAndFooterLayout from 'layouts/HeaderAndFooter';
import DonateAndCheckoutLayout from 'layouts/DonateAndCheckout';
import { Confirmation } from 'screens/Confirmation';
import { CheckoutBase } from 'screens/CheckoutBase';
import { getPageImageUrl } from 'helpers/imageUrls';
import { showToast } from 'components/Toast';
import { getChariotConnectId } from 'components/Slate/utils';
import { IRecipientIdEnum, RecipientsRequestType } from 'store/recipients/types';
import { getIsFeatureOn, MONTHLY_DONATE } from 'helpers/featureFlags';
import { oathRecurringIntervalFromStr } from 'screens/Checkout/Form/utils';
import { useRedirectAfterLogin } from 'hooks/useRedirectAfterLogin';
import { getDonateClickProcessor, trackDonationStarted, trackEvent } from 'helpers/mixpanelEvents';
import { usePartnerTrackingInit } from 'hooks/usePartnerTracking';
import { datadogLogs } from '@datadog/browser-logs';

const MAX_RECIPIENTS = 200;

const getPageData = (
  urlEvent: boolean,
  eventData?: IEvent,
  donatePageData?: IPage
): IPage | IEvent | undefined => {
  if (urlEvent && eventData) {
    return eventData;
  } else if (!urlEvent && donatePageData) {
    return donatePageData;
  } else {
    // no page data for search page
    return undefined;
  }
};

const getRecurringInterval = (urlRecurringInterval?: string, pageData?: IPage | IEvent) => {
  if (pageData?.recurring_enabled && pageData?.recurring_interval) {
    return pageData?.recurring_interval;
  }
  return oathRecurringIntervalFromStr(urlRecurringInterval || '');
};

export const MainScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleUpdateCredentials } = useRedirectAfterLogin();
  const location = useLocation();
  const urlParams = getUrlParams();
  const {
    urlTags,
    urlPartner,
    urlEvent,
    urlAmount,
    urlRecipients,
    urlRecurringInterval,
    urlShoppingCart: urlShoppingCartRaw,
  } = urlParams;

  const urlShoppingCart = urlShoppingCartRaw;
  const [isShoppingCartError, setIsShoppingCartError] = useState(false);
  const [processingRedirect, setProcessingRedirect] = useState<ProcessingRedirect>(
    ProcessingRedirectOptions.DE_OATH
  );
  const [pageType, setPageType] = useState<IPageType | undefined>();
  const [isRecurring, setIsRecurring] = useState(false);
  const [ticketSelections, setTicketSelections] = useState<TicketWithQuantitySelected[]>([]);
  const isOathCheckoutEnabled = useIsOathCheckoutEnabled();
  const {
    recipientsState,
    recipientsData,
    donatePageState,
    donatePageData,
    userState,
    donatModalConfig,
    eventState,
    eventData,
    dafData,
  } = useAppSelector(state => ({
    recipientsState: getRecipientsState(state),
    recipientsData: getRecipientsData(state),
    donatePageState: getDonatePageState(state),
    donatePageData: getDonatePageData(state),
    userState: getUserState(state),
    donatModalConfig: getDonateConfig(state, 'donate_modal'),
    eventState: getEventState(state),
    eventData: getEventData(state),
    dafData: getPayDafData(state),
  }));
  const [selectedDonationAmount, setSelectedDonationAmount] = useState<number | undefined>();
  // The amount the donor requested, before contribution limits cap it. Kept
  // alongside selectedDonationAmount (which holds the reconciled/charged total)
  // so CustomSplit can explain when the effective total was reduced by limits.
  const [requestedDonationAmount, setRequestedDonationAmount] = useState<number | undefined>();
  const [candidates, setCandidates] = useState<CandidateType[]>([]);
  const oathCheckout = useOathCheckout();
  const pageData = getPageData(urlEvent, eventData, donatePageData);
  usePartnerTrackingInit(pageData);
  const recurringInterval = getRecurringInterval(urlRecurringInterval, pageData);
  const requestLocationPath = location.pathname;
  const revalidatedForPage = useRef<string | undefined>(undefined);
  // Apply ?amount= only once per mount so a recipients re-fetch doesn't
  // override an amount the user already changed manually.
  const urlAmountApplied = useRef(false);
  const overageMax = getOverageMax(
    pageData?.allocation_type ?? 'EVEN',
    requestedDonationAmount,
    selectedDonationAmount,
    getMaxDonationAmountContributionLimits(getSelectedCandidates(candidates))
  );
  const showCustomSplit = getShowCustomSplit({
    allocationType: pageData?.allocation_type,
    candidates,
    overageMax,
  });

  const pageNotFound =
    donatePageState.error === 'No page found' || eventState.error?.includes('404');
  const isMissingPages =
    (urlEvent ? eventState.hasResolved : donatePageState.hasResolved) && !pageData;
  const hasApiError =
    (urlEvent ? eventState.error : donatePageState.error) || recipientsState.error;
  const selectionsLoading =
    !!urlRecipients && (!selectedDonationAmount || !getSelectedCandidates(candidates).length);
  const isLoadingPages = donatePageState.isLoading || eventState.isLoading || selectionsLoading;
  const headerVisible = !pageData || !pageData.whitelabeled;
  const auth0Requested = useAuth(() => dispatch(getUserProfile()));

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (userState.error) clearAccessToken();
  }, [userState]);

  useEffect(() => {
    if (!urlPartner) {
      navigate('/cart');
      return;
    }
    if (!getSessionID()) {
      setSessionID(createSessionID(10));
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchPageQueryStr = searchParams.get('p');
    const searchHasEvent = searchParams.has('event');
    if (!searchPageQueryStr || searchPageQueryStr === 'undefined') {
      return;
    }
    if (searchHasEvent && (!eventData || eventData.query_str !== searchPageQueryStr)) {
      dispatch(getEvent({ urlPartner: searchPageQueryStr }));
    } else if (!donatePageData || donatePageData.query_str !== searchPageQueryStr) {
      dispatch(getDonatePage({ urlPartner: searchPageQueryStr }));
    }
  }, [location.search]);

  const firedForPage = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Event payload comes entirely from pageData. Fire as soon as it's available.
    if (!pageData) {
      return;
    }
    if (firedForPage.current === pageData.query_str) return;
    firedForPage.current = pageData.query_str;
    trackEvent('Donate Page Viewed', {
      client_id: pageData?.client_id,
      is_stripe: pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE,
      allocation_type: pageData?.allocation_type ?? 'EVEN',
      tags: urlTags,
    });
  }, [pageData?.query_str]);

  useEffect(() => {
    if (!urlEvent && donatePageData?.event_id) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('event', '');
      // Full reload (not navigate) so the component remounts with urlEvent=true
      // and the initial mount effect dispatches getEvent.
      window.location.replace(`/donate?${searchParams.toString()}`);
      return;
    }

    if (pageData) {
      const isShoppingCartPage = pageData.page_category === DONATE_SHOPPING_CART_PAGE_CATEGORY;

      if (isShoppingCartPage) {
        // Shopping cart page: use recipient IDs from URL parameter
        if (!urlShoppingCart || urlShoppingCart.length === 0) {
          setIsShoppingCartError(true);
        } else {
          setIsShoppingCartError(false);
          // Always fetch recipients for shopping cart pages to ensure we have the correct ones
          dispatch(
            getRecipients({
              amount: MAX_RECIPIENTS,
              recipient_ids: urlShoppingCart,
              payment_processor: pageData?.processing_redirect,
              ...(pageData?.recipient_order_by && {
                order_by: pageData?.recipient_order_by,
              }),
              request_location_path: requestLocationPath,
              request_type: RecipientsRequestType.donate_page,
              page_query_str: urlPartner,
            })
          );
        }
      } else {
        // Not a shopping cart page: reset error state
        setIsShoppingCartError(false);
        if (urlPartner && !recipientsState.data) {
          // Regular partner page: use recipient IDs from page data
          dispatch(
            getRecipients({
              amount: MAX_RECIPIENTS,
              recipient_ids: pageData?.recipient_ids || [],
              payment_processor: pageData?.processing_redirect,
              request_location_path: requestLocationPath,
              request_type: RecipientsRequestType.donate_page,
              page_query_str: urlPartner,
              ...(pageData?.recipient_order_by && {
                order_by: pageData?.recipient_order_by,
              }),
            })
          );
        }
      }

      const updatedProcessingRedirect = getProcessingRedirect(pageData);
      const updatedPageType = pageData?.page_type;
      setProcessingRedirect(updatedProcessingRedirect);
      setPageType(updatedPageType);
    }
  }, [donatePageData, eventData]);

  useEffect(() => {
    // validate recipientsState and urlRecipients against pageData
    if (!pageData?.recipient_ids?.length) {
      return;
    }
    if (pageData.page_category === DONATE_SHOPPING_CART_PAGE_CATEGORY) {
      return;
    }
    if (recipientsState.isLoading) {
      return;
    }
    // Only validate when there is loaded state to check. This also makes the
    // mutual exclusion with the init effect explicit: that effect dispatches
    // getRecipients when !recipientsState.data; this effect only runs when
    // data is present, so the two dispatches cannot both fire on the same render.
    if (!recipientsState.data?.recipients?.length) {
      return;
    }

    const pageRecipientIds = new Set(pageData.recipient_ids);

    const stateHasInvalidRecipients =
      recipientsState.data.recipients.length !== pageData.recipient_ids.length ||
      recipientsState.data.recipients.some(r => !pageRecipientIds.has(r.recipient_id));
    // prevent re-fetch loop: skip if a re-fetch was already dispatched for this page. Log and fail silently instead.
    if (stateHasInvalidRecipients && revalidatedForPage.current !== pageData.query_str) {
      revalidatedForPage.current = pageData.query_str;
      datadogLogs.logger.info(
        'recipientIds in recipientsState do not match pageData.recipient_ids',
        {
          page_query_str: pageData.query_str,
          session_id: getSessionID(),
        }
      );
      // Use pageData.query_str (not urlPartner) — pageData is the source of truth in
      // this effect, and the two can diverge in the exact scenario being corrected here.
      dispatch(
        getRecipients({
          amount: MAX_RECIPIENTS,
          recipient_ids: pageData.recipient_ids,
          payment_processor: pageData.processing_redirect,
          request_location_path: requestLocationPath,
          request_type: RecipientsRequestType.donate_page,
          page_query_str: pageData.query_str,
          ...(pageData.recipient_order_by && {
            order_by: pageData.recipient_order_by,
          }),
        })
      );
    }

    if (urlRecipients && pageData.processing_redirect === ProcessingRedirectOptions.STRIPE) {
      const urlRecipientIds = urlRecipients
        .split(';')
        .map(r => r.split(':')[0])
        .filter(Boolean);
      const urlHasInvalidRecipients =
        urlRecipientIds.length > 0 && urlRecipientIds.some(id => !pageRecipientIds.has(id));
      if (urlHasInvalidRecipients) {
        datadogLogs.logger.info(
          'recipientIds in urlRecipients do not match pageData.recipient_ids',
          {
            page_query_str: pageData.query_str,
            url_recipients: urlRecipients,
            session_id: getSessionID(),
          }
        );
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('recipients');
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData, recipientsState.data, urlRecipients]);

  useEffect(() => {
    if (!recipientsData?.length) {
      return;
    }
    const {
      candidates: savedCandidates,
      total: savedDonationAmount,
      isRecurring: savedIsRecurring,
    } = pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE
      ? getSelectionsFromUrl(recipientsData, IRecipientIdEnum.OATH_ID)
      : getDonationSelectionsObj();

    const urlAmountNumber = Number(urlAmount);
    // Skip ?amount= on event pages and recommendation pages: REC pages start
    // with no recipients selected, so a pre-set total has nothing to allocate to.
    const shouldApplyUrlAmount =
      !urlAmountApplied.current &&
      !urlEvent &&
      pageData?.page_type !== 'REC' &&
      !!urlAmount &&
      !Number.isNaN(urlAmountNumber) &&
      urlAmountNumber >= 1;

    if (savedIsRecurring) {
      setIsRecurring(savedIsRecurring);
    }
    if (savedDonationAmount) {
      // Restore a saved selection as-is (its amounts were reconciled when saved); record
      // the total as the requested amount so a later selection change re-splits from it.
      setSelectedDonationAmount(Number(savedDonationAmount));
      setRequestedDonationAmount(Number(savedDonationAmount));
    } else if (shouldApplyUrlAmount) {
      urlAmountApplied.current = true;
      setRequestedDonationAmount(urlAmountNumber);
      setSelectedDonationAmount(urlAmountNumber);
    }
    if (
      savedCandidates?.length ||
      !candidates.length ||
      recipientsData.length !== candidates.length ||
      candidates.some(c => !recipientsData.find(r => r.recipient_id === c.recipient_id))
    ) {
      const updatedCandidates = getCandidates(recipientsData, savedCandidates, pageData?.page_type);
      if (!savedDonationAmount && shouldApplyUrlAmount) {
        // Split + reconcile the URL amount so the displayed total matches the capped
        // allocation (requestedDonationAmount is set above so the overage message can
        // compute and checkout isn't blocked by the mismatch).
        splitAndReconcile(urlAmountNumber, updatedCandidates);
      } else {
        setCandidates(updatedCandidates);
      }
    }
  }, [recipientsData]);

  // Fetch the donor's cycle giving for the visible recipients. Gated on
  // auth0Requested to ensure getContributionSummaries fires after user login
  useEffect(() => {
    if (!auth0Requested) return;
    const recipientIds = recipientsData?.map(r => r.recipient_id) ?? [];
    if (recipientIds.length) {
      dispatch(getContributionSummaries(recipientIds));
    }
  }, [recipientsData, auth0Requested]);

  useEffect(() => {
    if (selectedDonationAmount && candidates.length) {
      clearDonationSelections();
    }
  }, [selectedDonationAmount, candidates]);

  // allocates donations to candidates, then reconciles the displayed/charged total down to
  // what was actually allocated. Candidate contribution limits can cap an EVEN split
  // below the amount and drop the overage.
  const splitAndReconcile = (amount: number | undefined, candidatesToSplit: CandidateType[]) => {
    const allocatedCandidates = computeDefaultSplitAllocation(
      amount,
      pageData ?? { allocation_type: 'EVEN', recipient_groups: [] },
      candidatesToSplit
    );
    setCandidates(allocatedCandidates);
    setSelectedDonationAmount(
      reconcileEvenSplitTotal(amount, allocatedCandidates, pageData?.allocation_type)
    );
  };

  const onUserDonationTotalChange = (amount?: number) => {
    setRequestedDonationAmount(amount);
    splitAndReconcile(amount, candidates);

    trackEvent('Donation Amount Changed', {
      is_recurring: isRecurring,
      client_id: pageData?.client_id,
      amount: amount,
      is_stripe: pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE,
    });
  };

  // A selection change re-splits the current total for the new selection and reconciles
  // (revising down when limits cap it).
  const onSelectCandidates = (updatedCandidates: CandidateType[]) => {
    splitAndReconcile(selectedDonationAmount, updatedCandidates);
  };

  const handleCandidatesSelected = (index: number, checkValue: boolean) => {
    const updatedCandidates = [...candidates];
    updatedCandidates[index] = {
      ...candidates[index],
      isSelected: checkValue,
    };
    onSelectCandidates(updatedCandidates);
    trackEvent('Recipient Selection Changed', {
      recipient_id: candidates[index].recipient_id,
      is_selected: checkValue,
    });
  };

  const onDafDonateClick = () => {
    trackEvent('Donate Button Clicked', {
      client_id: pageData?.client_id,
      is_recurring: isRecurring,
      is_stripe: pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE,
      payment_processor: 'DAF',
      allocation_type: pageData?.allocation_type ?? 'EVEN',
      // DAF is single-recipient by construction (`getChariotConnectId`), so there's no split to evaluate.
      used_default_split: null,
    });
    if (!selectedDonationAmount || selectedDonationAmount < 1)
      showToast(`Donation amount must be greater than ${formatNumberAsCurrency(1)}`, 'error');
    else navigate(`/donate/checkout/daf?p=${urlPartner}`);
  };

  const onDonateClick = (selectedMethod?: string, selectedMethodType?: string) => {
    try {
      let usedDefaultSplit: boolean | null = null;
      if (selectedDonationAmount && pageData) {
        const defaultCandidates = computeDefaultSplitAllocation(
          selectedDonationAmount,
          pageData,
          candidates
        );
        usedDefaultSplit = candidates.every(
          (c, i) => c.donationAmount === defaultCandidates[i].donationAmount
        );
      }
      const isStripePage = pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE;

      trackEvent('Donate Button Clicked', {
        client_id: pageData?.client_id,
        is_recurring: isRecurring,
        is_stripe: isStripePage,
        payment_processor: getDonateClickProcessor(selectedMethod, pageData?.processing_redirect),
        allocation_type: pageData?.allocation_type ?? 'EVEN',
        used_default_split: usedDefaultSplit,
      });
    } catch {
      noop();
    }
    const selectedCandidates = getSelectedCandidatesWithValidAmount(candidates);
    const ticketPriceTotal = getTicketPriceTotal(ticketSelections);
    const isDonationValid = validateDonation({
      clientId: pageData?.client_id,
      isRecurring: isRecurring,
      selectedCandidates,
      selectedDonationAmount,
      allCandidates: candidates,
      ticketPriceTotal,
    });
    if (!isDonationValid || !selectedDonationAmount) {
      return;
    }
    if (urlEvent) {
      if (ticketPriceTotal && selectedDonationAmount < ticketPriceTotal) {
        datadogLogs.logger.info(
          'Donation is less than selected ticket price due to contribution limits',
          {
            page_query_str: pageData?.query_str,
            event_id: eventData?.event_id,
            ticket_price_total: ticketPriceTotal,
            donation_amount: selectedDonationAmount,
            tickets_selected: getTicketsWithSelections(ticketSelections).map(t => ({
              name: t.name,
              price: t.price,
              quantity_selected: t.quantity_selected,
            })),
            session_id: getSessionID(),
          }
        );
      }
    }
    trackDonationStarted(selectedDonationAmount, candidates);
    if (selectedMethod === 'pledge') {
      navigate(
        getPathWithRef(
          `/donate/checkout/standard?${getUrlSearchParamsStrNoMethod()}&m=${selectedMethod}&mt=${selectedMethodType}`
        )
      );
      return;
    } else if (pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE) {
      if (isRecurring && !userState.data?.email) {
        const checkoutPage = urlEvent ? eventData : donatePageData;
        if (!checkoutPage) {
          return;
        }
        const checkoutLink = new URL(getCustomCheckoutLink(checkoutPage, candidates, true));
        handleUpdateCredentials(`${checkoutLink.pathname}${checkoutLink.search}`);
        return;
      }
      navigate(getPathWithRef(`/donate/checkout/standard?${getUrlSearchParamsStrNoMethod()}`));
      return;
    } else if (isOathCheckoutEnabled) {
      oathCheckout(selectedCandidates, selectedDonationAmount);
    } else if (
      processingRedirect === ProcessingRedirectOptions.DE_GENERAL ||
      processingRedirect === ProcessingRedirectOptions.DE_OATH
    ) {
      democracyEngineCheckoutRedirect(selectedCandidates, selectedDonationAmount, '', []);
    } else if (processingRedirect === ProcessingRedirectOptions.EN_GENERAL) {
      engageLabsCheckoutRedirect(selectedCandidates, selectedDonationAmount);
    }
  };

  useEffect(() => {
    if (dafData) {
      navigate('/donate/confirmation/daf');
    }
  }, [dafData]);

  return (
    <HeaderAndFooterLayout
      footerVisible={true}
      headerVisible={headerVisible}
      footerWhiteLabeled={!!pageData?.whitelabeled}
      isLoading={!hasApiError && isLoadingPages}
    >
      <Routes>
        <Route
          path="/"
          element={
            <DonateAndCheckoutLayout
              error={
                pageNotFound
                  ? 'Sorry, the page you are looking for does not exist. Please check the URL and try again.'
                  : hasApiError
                    ? 'An error has occurred. Contact us.'
                    : isMissingPages
                      ? 'Donate page data not found.'
                      : isShoppingCartError
                        ? 'Shopping cart data not found. Please select recipients from the search page.'
                        : undefined
              }
              shouldAlertOnError={!!hasApiError}
              showPausedModal={pageData?.status === 'PAUSED'}
              showLeftSide={pageType !== 'MEDIA'}
              showDonateModal={
                pageData?.status === 'ACTIVE' &&
                pageData?.owner === 'OATH' &&
                donatModalConfig?.donate_modal_config === 'on' &&
                donatModalConfig?.donate_modal_link &&
                !donatModalConfig?.donate_modal_link?.includes(`p=${urlPartner}`)
              }
              pausedMisc={pageData?.paused_misc}
            />
          }
        >
          <Route
            index
            element={
              <div className="full-width">
                {urlEvent ? (
                  <EventSlate
                    candidates={candidates}
                    eventData={eventData}
                    handleCandidatesSelected={handleCandidatesSelected}
                    onDonateClick={onDonateClick}
                    onSelectCandidates={onSelectCandidates}
                    onUserDonationTotalChange={onUserDonationTotalChange}
                    selectedDonationAmount={selectedDonationAmount}
                    overageMax={overageMax}
                    setCandidates={setCandidates}
                    setSelectedDonationAmount={setSelectedDonationAmount}
                    setTicketSelections={setTicketSelections}
                    showCustomSplit={showCustomSplit}
                    ticketSelections={ticketSelections}
                    type={pageType || 'LIST'}
                  />
                ) : (
                  <Slate
                    page={pageData}
                    recipients={recipientsData}
                    candidates={candidates}
                    onSelectCandidates={onSelectCandidates}
                    handleCandidatesSelected={handleCandidatesSelected}
                    selectedDonationAmount={selectedDonationAmount}
                    overageMax={overageMax}
                    onDonateClick={onDonateClick}
                    onUserDonationTotalChange={onUserDonationTotalChange}
                    setCandidates={setCandidates}
                    setSelectedDonationAmount={setSelectedDonationAmount}
                    showCustomSplit={showCustomSplit}
                    type={pageType || 'LIST'}
                    onDafDonateClick={onDafDonateClick}
                    isDafEnabled={
                      pageData?.subscription_level === 'PREMIUM' &&
                      !!getChariotConnectId(candidates)
                    }
                    isRecurring={isRecurring}
                    setIsRecurring={setIsRecurring}
                    recurringInterval={recurringInterval}
                    isMonthlyEnabled={
                      pageData?.processing_redirect === ProcessingRedirectOptions.STRIPE &&
                      (getIsFeatureOn(MONTHLY_DONATE) || pageData?.recurring_enabled)
                    }
                    enableScrollToDonate={pageType === 'COMPACT'}
                  />
                )}
              </div>
            }
          />
          <Route
            path="checkout/*"
            element={
              <CheckoutBase
                candidates={candidates}
                clientId={pageData?.client_id}
                clientName={pageData?.client_name}
                donationAmount={selectedDonationAmount || 0}
                isRecurring={isRecurring}
                pageId={pageData?.id}
                pageName={pageData?.value}
                pageOwner={pageData?.owner ?? 'Unknown'}
                pageQueryString={pageData?.query_str}
                recurringInterval={recurringInterval}
                ticketSelections={ticketSelections}
              />
            }
          />
        </Route>
        <Route
          path="/confirmation/*"
          element={
            <Confirmation
              candidates={getSelectedCandidates(candidates)}
              donationAmount={selectedDonationAmount || 0}
              page={pageData}
              isEvent={urlEvent}
              isRecurring={isRecurring}
              pageImage={pageData ? getPageImageUrl(pageData) : ''}
              recurringInterval={recurringInterval}
            />
          }
        />
      </Routes>
    </HeaderAndFooterLayout>
  );
};
